import { toSignificant } from '@simplywallst/formatting';
import DataTable from '@components/DataTable';
import { SWS_ANALYSIS_MODEL } from '@/constants';

interface REITModelProps {
  uniqueSymbol: string;
  reportingCurrencyISO: string;
  companyShortName: string;
  relativeListingSharesOutstanding: number;
  npvPerShare: number;
  REIT: {
    NAV: number;
    NAVCount: number;
  };
}

const REITModel = ({
  uniqueSymbol,
  relativeListingSharesOutstanding,
  reportingCurrencyISO,
  companyShortName,
  npvPerShare,
  REIT,
}: REITModelProps) => (
  <div>
    <p>
      {`In the case of Real Estate Investment Trusts (REITs) like ${companyShortName} our preference is to use estimates of Adjusted Funds from Operations (AFFO) to calculate the intrinsic value using a discounted cash flow calculation.`}
    </p>
    <p>
      {`However, in the case of ${companyShortName} these estimates are not available, instead we are able to use analyst estimates of Net Asset Value (NAV).`}
    </p>
    <p>
      The Net Asset Value (NAV) model is the most common intrinsic valuation
      methodology for REITs. The underlying idea is that you can value a REIT by
      valuing its real estate assets and then adjusting for its other assets and
      subtracting liabilities.
    </p>
    <p>
      <span>See our </span>
      <strong>
        <a
          href={`${SWS_ANALYSIS_MODEL}/MODEL.markdown#adjusted-funds-from-operations-discounted-cash-flows-model--how-is-this-calculated`}
          target="_blank"
          rel="noopener noreferrer nofollow"
        >
          documentation
        </a>
      </strong>
      <span> to learn about this calculation.</span>
    </p>
    <DataTable light>
      <caption>{`${uniqueSymbol} Net Asset Value (NAV)`}</caption>
      <thead>
        <tr>
          <th />
          <th>Calculation</th>
          <th>Result</th>
        </tr>
      </thead>
      <tbody>
        <tr>
          <td>
            {`Average NAV from ${toSignificant(
              REIT.NAVCount,
              2
            )} analysts: ${reportingCurrencyISO}${toSignificant(REIT.NAV, 2)}`}
          </td>
          <td>
            {`= ${reportingCurrencyISO}${toSignificant(
              REIT.NAV,
              2
            )} / ${toSignificant(relativeListingSharesOutstanding, 2)}`}
          </td>
          <td>{`${reportingCurrencyISO}${toSignificant(npvPerShare, 2)}`}</td>
        </tr>
      </tbody>
    </DataTable>
  </div>
);

export default REITModel;
